/**
 * <site-header> — sticky behaviour, desktop mega menu and the mobile menu panel.
 *
 * Sticky: the header stays pinned to the top of the viewport for the whole page, which is what
 * SAVETTE-35 asks for. It never hides on scroll.
 *
 * Mega menu: each top-level item that has children carries [data-menu-trigger] and points at its
 * panel with data-panel. Pointer enter and keyboard focus open the panel; the pointer leaving the
 * nav and panel, or Escape, closes it, as live does. The backdrop lets a mouse pointer through
 * (sections/header.liquid), so pointerleave fires the moment the pointer reaches the page; on a
 * touch-only screen the backdrop still takes the tap, and its click closes the panel.
 *
 * Mobile: [data-mobile-toggle] opens the full-screen panel, whose tab row is a <savette-selector>
 * (assets/selector.js) — its selector:change event swaps the visible submenu.
 */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.panels = [...this.querySelectorAll('[data-panel-id]')];
    this.triggers = [...this.querySelectorAll('[data-menu-trigger]')];
    this.mobileToggle = this.querySelector('[data-mobile-toggle]');
    this.mobilePanel = this.querySelector('[data-mobile-panel]');

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('pointerenter', this.onTriggerEnter);
      trigger.addEventListener('focus', this.onTriggerEnter);
      trigger.addEventListener('click', this.onTriggerClick);
    });
    this.addEventListener('pointerleave', this.closeMenu);
    this.addEventListener('keydown', this.onKeydown);
    // Only reachable on a touch-only screen: with a mouse the backdrop is pointer-events: none.
    this.querySelector('[data-menu-backdrop]')?.addEventListener('click', this.closeMenu);

    this.mobileToggle?.addEventListener('click', this.toggleMobile);
    this.mobilePanel?.addEventListener('selector:change', this.onTabChange);

    this.publishHeight();
    window.addEventListener('resize', this.publishHeight, { passive: true });
    document.addEventListener('drawer:open', this.onDrawerChange);
    document.addEventListener('drawer:close', this.onDrawerChange);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.publishHeight);
    document.removeEventListener('drawer:open', this.onDrawerChange);
    document.removeEventListener('drawer:close', this.onDrawerChange);
    this.unlockScroll();
  }

  /**
   * The drawers open below the header on small screens, so they need its height. Two values
   * are published: the whole header including the announcement banner (--header-total), and
   * the navigation bar alone (--header-height), which is what the live site measures its
   * first-module height against and what the mobile panel's top padding reads.
   */
  publishHeight = () => {
    const root = document.documentElement.style;
    root.setProperty('--header-total', `${Math.round(this.offsetHeight)}px`);
    const bar = [...this.querySelectorAll('.header__bar')].find((el) => el.offsetHeight > 0);
    if (bar) root.setProperty('--header-height', `${Math.round(bar.offsetHeight)}px`);
  };

  /**
   * While a drawer is open the matching header label reads Close and closes it, and logs as a
   * close (cart_close) rather than an open; the attribute changes after the click that toggled
   * the drawer has been logged. The drawers that open below the header on a phone (search,
   * account, size guide; the bag covers the header) read --header-total for their top, so it is
   * republished from where the header's bottom edge actually is on screen: the full height while
   * the header is pinned, less (down to 0) if a non-sticky header has scrolled up out of view.
   */
  onDrawerChange = (event) => {
    const drawer = event.target.closest('site-drawer');
    const name = drawer?.getAttribute('name');
    if (!name) return;
    const open = event.type === 'drawer:open';
    if (open) {
      const bottom = Math.max(0, this.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty('--header-total', `${Math.round(bottom)}px`);
    } else {
      this.publishHeight();
    }
    this.classList.toggle(`header--drawer-${name}`, open);
    if (name === 'cart') {
      // assets/drawer.js dispatches drawer:open and drawer:close synchronously inside the click on
      // Bag / Close, before that click has finished bubbling. Flipping the event name now would let
      // a delegated analytics listener read the new value, so opening would log cart_close. The
      // write waits for a new task, after the click's dispatch is over: the press that opens logs
      // cart_open, and the press that closes logs cart_close.
      setTimeout(() => {
        this.querySelectorAll('.header__cart').forEach((link) =>
          link.setAttribute('data-analytics-event', open ? 'cart_close' : 'cart_open'),
        );
      }, 0);
    }
  };

  onTriggerEnter = (event) => {
    // Escape moves focus back to the trigger; without this the focus would reopen the panel
    // that Escape just closed.
    if (this.suppressFocusOpen && event.type === 'focus') return;
    const trigger = event.currentTarget;
    this.openMenu(trigger.dataset.menuTrigger);
  };

  // A tap on a touch device opens the panel rather than following the link straight away.
  onTriggerClick = (event) => {
    const trigger = event.currentTarget;
    if (trigger.getAttribute('aria-expanded') === 'true') return;
    if (window.matchMedia('(any-pointer: fine)').matches) return;
    event.preventDefault();
    this.openMenu(trigger.dataset.menuTrigger);
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') {
      if (this.mobilePanel && !this.mobilePanel.hidden) {
        this.toggleMobile();
        this.mobileToggle?.focus();
        return;
      }
      const expanded = this.triggers.find((t) => t.getAttribute('aria-expanded') === 'true');
      this.suppressFocusOpen = true;
      this.closeMenu();
      expanded?.focus();
      requestAnimationFrame(() => {
        this.suppressFocusOpen = false;
      });
      return;
    }

    const trigger = event.target.closest('[data-menu-trigger]');
    const panel = event.target.closest('[data-panel-id]');

    // From a top-level item, Down or Tab walks into the panel it just opened.
    if (trigger && (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey))) {
      const links = this.panelLinks(trigger.dataset.menuTrigger);
      if (links.length === 0) return;
      event.preventDefault();
      this.openMenu(trigger.dataset.menuTrigger);
      links[0].focus();
      return;
    }

    if (!panel) return;
    const links = [...panel.querySelectorAll('a[href]')];
    const index = links.indexOf(event.target);
    const panelTrigger = this.triggers.find((t) => t.dataset.menuTrigger === panel.dataset.panelId);

    // Shift+Tab or Up from the first link goes back to the item that opened the panel.
    if (index === 0 && ((event.key === 'Tab' && event.shiftKey) || event.key === 'ArrowUp')) {
      event.preventDefault();
      panelTrigger?.focus();
      return;
    }

    // Tabbing past the last link closes the panel and carries on through the page.
    if (index === links.length - 1 && event.key === 'Tab' && !event.shiftKey) {
      this.closeMenu();
    }
  };

  panelLinks(id) {
    const panel = this.panels.find((p) => p.dataset.panelId === id);
    return panel ? [...panel.querySelectorAll('a[href]')] : [];
  }

  openMenu(id) {
    this.panels.forEach((panel) => (panel.hidden = panel.dataset.panelId !== id));
    this.triggers.forEach((trigger) =>
      trigger.setAttribute('aria-expanded', trigger.dataset.menuTrigger === id ? 'true' : 'false'),
    );
    this.classList.add('header--menu-open');
  }

  closeMenu = () => {
    if (!this.classList.contains('header--menu-open')) return;
    this.panels.forEach((panel) => (panel.hidden = true));
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    this.classList.remove('header--menu-open');
  };

  toggleMobile = () => {
    if (!this.mobilePanel) return;
    const open = this.mobilePanel.hidden;
    this.mobilePanel.hidden = !open;
    this.mobileToggle.setAttribute('aria-expanded', String(open));
    this.classList.toggle('header--mobile-open', open);
    if (open) this.lockScroll();
    else this.unlockScroll();
  };

  onTabChange = (event) => {
    const value = event.detail.value;
    if (!value) return;
    this.mobilePanel
      .querySelectorAll('[data-mobile-submenu]')
      .forEach((panel) => (panel.hidden = panel.dataset.mobileSubmenu !== value));
  };

  lockScroll() {
    document.documentElement.style.overflow = 'hidden';
  }

  unlockScroll() {
    document.documentElement.style.overflow = '';
  }
}

if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);
